import DOMPurify from 'dompurify';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef
} from 'react';
import {
    useEditor,
    Editor,
    Extensions
} from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Link from '@tiptap/extension-link';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import Text from '@tiptap/extension-text';
import {
    EditorEvents,
    Extension
} from '@tiptap/core';
import {
    FeedbackNode,
    MultiSelectNode,
    OptionNode,
    ProductNode,
    RatingNode,
    SelectNode
} from './ChatBotExtensions';
import { allowedLinkOrigins } from './allowedLinkOrigins';

export const isAllowedUrl = (url: string) => {
    try {
        return allowedLinkOrigins.has(new URL(url).origin);
    } catch (err) {
        return false;
    }
};

const sanitizeHtml = (html: string) =>
    DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        ADD_TAGS: [
            'feedback',
            'product',
            'rating',
            'multiselect'
        ],
        ADD_ATTR: [
            'chatbot-session-id',
            'desc',
            'image-url',
            'markdown-price',
            'price',
            'slug',
            'submitLabel',
            'subtext',
            'subtitle',
            'showRating',
            'ratingCount',
            'title',
            'variant',
        ],
    })

const submit = (
    editor: Editor | null,
    submitter: (text: string) => Promise<void>,
) => {
    if (editor && !editor.isDestroyed) {
        const text = editor.getText().trim();
        if (text) {
            submitter(text);
        }
        return true;
    }
    return false;
};

const createKeymap = (
    submitEditorMessage: (editor: Editor | null) => boolean,
) => {
    return Extension.create({
        name: 'chat_keymap',
        addKeyboardShortcuts() {
            return {
                Enter: () => {
                    submitEditorMessage(this.editor as Editor);
                    return true;
                },
            }
        },
    });
};

export type TipTapEditorProps = {
    placeholder?: string;
    submitter: (text: string) => Promise<void>;
    onUpdate?: (props: EditorEvents['update']) => void;
    sendMessage: (text: string) => Promise<void>;
};

export function useTipTapEditor (props: TipTapEditorProps) {
    const {
        placeholder,
        submitter,
        onUpdate,
    } = props;

    const submitterRef = useRef(submitter);
    useEffect(() => {
        submitterRef.current = submitter;
    }, [submitter]);

    const submitEditorMessage = useCallback((editor: Editor | null) => {
        if (submitterRef.current) {
            return submit(editor, submitterRef.current);
        }
        return false;
    }, [submitterRef]);

    const extensions = useMemo(() => {
        const keymap = createKeymap(submitEditorMessage);

        const extensions: Extensions = [
            Document,
            Paragraph,
            Text,
            Placeholder.configure({ placeholder }),
            keymap,
        ];

        return extensions;
    }, [submitEditorMessage, placeholder]);

    const editor = useEditor({
        extensions,
        onUpdate: (props) => {
            if (onUpdate) {
                onUpdate(props);
            }
        },
    });

    const focus = useCallback(() => {
        editor?.commands.focus();
    }, [editor]);

    const clear = useCallback(() => {
        editor?.commands.clearContent();
    }, [editor]);

    return {
        editor,
        submit: () => submitEditorMessage(editor),
        focus,
        clear,
        characterCount: editor?.getText().length ?? 0,
    };
}

export interface UseTipTapViewerProps {
    message: string;
    sendMessage: (text: string) => Promise<void>;
    textOnly: boolean;
    isActionable?: boolean;
    selectedOption?: string;
}

export function useTipTapViewer ({
    message,
    sendMessage,
    textOnly,
    isActionable,
    selectedOption,
}: UseTipTapViewerProps) {
    const purifiedHtml = useMemo(() => sanitizeHtml(message || ''), [message]);
    // Conditionally load extensions
    const extensions = useMemo(() => {
        if (textOnly) {
            // Customer: Only Paragraph and Text
            return [
                Document,
                Paragraph,
                Text,
            ];
        } else {
            // Agent/Bot: Full-featured set
            return [
                StarterKit,
                Link.configure({
                    isAllowedUri: (url, ctx) => ctx.defaultValidate(url) && isAllowedUrl(url),
                }),
                FeedbackNode.configure({
                    sendMessage,
                    isActionable,
                }),
                MultiSelectNode.configure({
                    sendMessage,
                    isActionable,
                    selectedOption,
                }),
                OptionNode,
                ProductNode,
                SelectNode.configure({
                    sendMessage,
                    isActionable,
                    selectedOption,
                }),
                RatingNode.configure({
                    sendMessage,
                    isActionable,
                    selectedOption,
                }),
            ];
        }
    }, [textOnly, sendMessage, isActionable, selectedOption]);


    const editor = useEditor({
        extensions,
        content: purifiedHtml,
        editable: false,
    }, [extensions, purifiedHtml]);

    return editor;
}
